// The ONE form that raises a SAFed test job (Session BDC). The lift page's panel and the office's "Raise work" entry points
// (the Scheduling page's Raise..., the lift, site and customer "Raise work" menus) all render this, so there is no second path with
// different rules: it calls the same POST /api/safed-jobs (raise_safed_test_job) and nothing here sends anything or raises an invoice.
//
// A lift with no recommendation and no quotation is not blocked: the form says so and points at the lift page, where one is recorded.
// The engineer and the day are optional; the job can be booked later on the Scheduling page.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../../lib/api";
import type { SafedQuoteView, SafedRecommendationView } from "../../../../lib/safedFlowTypes";
import { btnCls, errorText, inputCls } from "./safedUi";

type CatalogueTest = { id: string; name: string; cls: "STANDARD" | "STRESS"; suggested?: boolean };

export function SafedRaiseForm({
  assetId,
  onRaised,
  onCancel,
  quotes: quotesProp,
  recommendations: recsProp,
}: {
  assetId: string;
  onRaised: (jobId: string | null) => void;
  onCancel: () => void;
  // The lift page already holds these and passes them down; a host that does not (the Raise work modal) lets the form read them.
  quotes?: SafedQuoteView[];
  recommendations?: SafedRecommendationView[];
}) {
  const [catalogue, setCatalogue] = useState<CatalogueTest[]>([]);
  const [quotes, setQuotes] = useState<SafedQuoteView[]>(quotesProp ?? []);
  const [recs, setRecs] = useState<SafedRecommendationView[]>(recsProp ?? []);
  const [engineers, setEngineers] = useState<{ id: string; name: string }[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [callForKind, setCallForKind] = useState<"examiner_report" | "customer_request">("examiner_report");
  const [callForRef, setCallForRef] = useState("");
  const [quoteRef, setQuoteRef] = useState("");
  const [engineerId, setEngineerId] = useState("");
  const [day, setDay] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoaded(false);
    setPicked([]);
    setQuoteRef("");
    setCallForRef("");
    void (async () => {
      try {
        const [t, flow, so] = await Promise.all([
          api.safedJobs.tests(assetId),
          quotesProp && recsProp ? Promise.resolve(null) : api.safedFlow.lift(assetId),
          api.safedFlow.signoffs().catch(() => null),
        ]);
        if (!live) return;
        setCatalogue(t.tests.map((x) => ({ id: x.id, name: x.name, cls: x.cls })));
        if (flow) {
          setQuotes(flow.quotes);
          setRecs(flow.recommendations);
        }
        setEngineers(so?.engineers ?? []);
        setError(null);
      } catch {
        if (live) setError("Could not load the tests for this lift.");
      } finally {
        if (live) setLoaded(true);
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the props are synced by the effect below; a parent reload must not wipe the form
  }, [assetId]);

  useEffect(() => {
    if (quotesProp) setQuotes(quotesProp);
    if (recsProp) setRecs(recsProp);
  }, [quotesProp, recsProp]);

  const quotable = quotes.filter((q) => q.status !== "declined" && q.jobIds.length === 0);
  const nothingRecorded = loaded && quotable.length === 0 && recs.filter((r) => !r.dismissedAt).length === 0;

  // Raise from a quotation: the tests, and the call-for source, follow it.
  function chooseQuote(ref: string) {
    setQuoteRef(ref);
    const q = quotes.find((x) => x.reference === ref);
    if (q) {
      setPicked(q.tests.map((t) => t.id));
      const rec = recs.find((r) => r.id === q.recommendationId);
      if (rec) {
        setCallForKind(rec.source === "customer_request" ? "customer_request" : "examiner_report");
        setCallForRef(rec.documentRef ?? rec.recommendedText.slice(0, 200));
      }
    }
  }

  async function raise() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.safedJobs.raise({
        assetId,
        testIds: picked,
        callForKind,
        callForRef: callForRef.trim() || null,
        quoteRef: quoteRef.trim() || null,
        scheduledDay: day || null,
        engineerId: engineerId || null,
        preview: false,
      });
      onRaised(res.jobId ?? null);
    } catch (err) {
      setError(errorText(err, "Could not raise the SAFed test job."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-3" data-testid="safed-raise-form">
      <p className="text-sm text-ele-body">Choose the tests. A stress test cannot start without the duty holder&apos;s signed authorisation, two engineers and an engineer signed off for it.</p>
      {nothingRecorded && (
        <p role="status" className="mt-2 rounded-ele border border-ele-amber-line bg-ele-amber-wash px-3 py-2 text-sm text-ele-strong">
          This lift has no SAFed recommendation or quotation recorded yet. You can still raise the job, or{" "}
          <Link className="underline" to={`/office/lifts/${assetId}#safed`}>record a recommendation or a quotation on the lift page</Link> first.
        </p>
      )}
      {quotable.length > 0 && (
        <label className="mt-2 block text-sm text-ele-body">
          From a quotation
          <select className={inputCls} value={quoteRef} onChange={(e) => chooseQuote(e.target.value)}>
            <option value="">None</option>
            {quotable.map((q) => (
              <option key={q.id} value={q.reference}>{q.reference} ({q.status === "accepted" ? "accepted" : "not yet accepted"})</option>
            ))}
          </select>
        </label>
      )}
      <ul data-vscroll className="mt-2 max-h-56 space-y-1 overflow-auto">
        {catalogue.map((t, n) => (
          <li key={t.id}>
            {/* Every test is offered: the drive only suggests (LG1 3.2, irrespective of drive system), so the usual ones come first. */}
            {t.suggested === false && (n === 0 || catalogue[n - 1]?.suggested !== false) && <p className="mt-2 text-xs text-ele-muted">Other tests, not usual for this lift's drive (any test can be added)</p>}
            <label className="flex min-h-10 items-center gap-2 text-sm text-ele-strong">
              <input type="checkbox" checked={picked.includes(t.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, t.id] : picked.filter((p) => p !== t.id))} />
              {t.id} {t.name}{t.cls === "STRESS" ? " (stress test, ELE risk label)" : ""}
            </label>
          </li>
        ))}
      </ul>
      <label className="mt-3 block text-sm text-ele-body">
        Where the test comes from
        <select className={inputCls} value={callForKind} onChange={(e) => setCallForKind(e.target.value as typeof callForKind)}>
          <option value="examiner_report">The examiner&apos;s report (the Competent Person calls for it)</option>
          <option value="customer_request">The customer&apos;s own request</option>
        </select>
      </label>
      <label className="mt-2 block text-sm text-ele-body">
        {callForKind === "examiner_report" ? "Report reference and LG1 clause" : "Date and wording of the request"}
        <input className={inputCls} value={callForRef} onChange={(e) => setCallForRef(e.target.value)} />
      </label>
      <label className="mt-2 block text-sm text-ele-body">
        Quotation reference (chosen above, or typed if it is on another system)
        <input className={inputCls} value={quoteRef} onChange={(e) => setQuoteRef(e.target.value)} />
      </label>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <label className="block text-sm text-ele-body">
          Engineer (optional)
          <select className={inputCls} value={engineerId} onChange={(e) => setEngineerId(e.target.value)}>
            <option value="">Not decided yet</option>
            {engineers.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm text-ele-body">
          Day (optional)
          <input type="date" className={inputCls} value={day} onChange={(e) => setDay(e.target.value)} />
        </label>
      </div>
      <p className="mt-2 text-xs text-ele-muted">Raising the job sends nothing and raises no invoice. You can book the second engineer and the day on the Scheduling page once it is raised.</p>
      {error && <p role="alert" className="mt-2 text-sm text-ele-danger">{error}</p>}
      <div className="mt-3 flex gap-2">
        <button type="button" className={btnCls} disabled={busy || picked.length === 0} onClick={() => void raise()}>Raise the job</button>
        <button type="button" className="min-h-11 px-3 text-sm text-ele-muted" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
